import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, Divider, LinearProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { useEmployeeService } from "../../services/EmployeeService";
import FilterHeader from "../../components/FilterHeader";

const ROW_HEIGHT = 32;

const DetailedAttendanceLogList = ({ empID = "All" }) => {
  const { fetchDetailedAttendanceLogs } = useEmployeeService();

  const [filters, setFilters] = useState({
    VerticalDescriptions: "All",
    ProjectDescriptions: "All",
    Locations: "All",
  });

  const [search, setSearch] = useState({});
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const listRef = useRef();

  const columnDefs = useMemo(() => {
    const base = [
      { key: "SessionStart", label: "Check In", flex: 1.5 },
      { key: "SessionEnd", label: "Check Out", flex: 1.5 },
      { key: "AMS_SyncedOn", label: "AMS Synced On", flex: 1.5 },
      { key: "PS_UUId_Checkin", label: "Check-in UUID", flex: 2.5 },
      { key: "CheckInPushedOn", label: "Check-in Pushed On", flex: 1.5 },
      { key: "PS_UUId_CheckOut", label: "Check-out UUID", flex: 2.5 },
      { key: "CheckOutPushedOn", label: "Check-out Pushed On", flex: 1.5 },     
    ];
    if (empID === "All") {
      base.unshift(
        { key: "UserGuid", label: "User ID", flex: 1 },
        { key: "Full_Name", label: "Name", flex: 2 }
      );
    }
    return base;
  }, [empID]);

  const fetchData = async () => {
    if (startDate.isAfter(endDate)) {
      alert("Start Date must be before or equal to End Date");
      return;
    }

    setLoading(true);
    try {
      const res = await fetchDetailedAttendanceLogs({
        employeeId: empID,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });

      const filtered = empID === "All"
        ? res
        : res.filter(d => d.UserGuid === empID);

      setData(filtered);
    } catch (err) {
      setError(err.message || "Failed to fetch detailed logs");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchVertical = filters.VerticalDescriptions === "All" || item.Business_Vertical_Desc === filters.VerticalDescriptions;
      const matchProject = filters.ProjectDescriptions === "All" || item.Project_Description === filters.ProjectDescriptions;
      const matchLocation = filters.Locations === "All" || item.Business_Sub_Vertical_Description === filters.Locations;

      const matchesSearch = columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        if (!term) return true;
        const value = item[key];
        return value?.toString().toLowerCase().includes(term);
      });

      return matchVertical && matchProject && matchLocation && matchesSearch;
    });
  }, [data, filters, search, columnDefs]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    return (
      <Box key={index} style={style} sx={{ display: "flex", alignItems: "center", borderBottom: "0.5px solid #eee", px: 0.8 }}>
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
              fontSize: "0.775rem",
            }}
          >
            {row[key]}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h2" sx={{ minWidth: 180 }}>
          Detailed Attendance Logs
        </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              {/* {empID === "All" && ( 
                     <FilterHeader filters={filters} setFilters={setFilters} /> 
            )} */}
          <DatePicker
            label="Start Date"
            value={startDate}
            format="DD-MMM-YYYY"
            onChange={(date) => setStartDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            format="DD-MMM-YYYY"
            onChange={(date) => setEndDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button variant="contained" onClick={fetchData}>Fetch Logs</Button>
        </Box>
      </Box>

     
      <Divider sx={{ my: 0.5 }} />

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error">Error: {error}</Typography>
      ) : (
        <Box component={Paper} sx={{ overflow: "auto" }}>
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) => setSearch((prev) => ({ ...prev, [key]: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>

          {filteredData.length > 0 ? (
            <Box sx={{ height: "70vh" }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemSize={() => ROW_HEIGHT}
                    itemCount={filteredData.length}
                    itemKey={(index) => filteredData[index].UserGuid + index}
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          ) : (
            <Box p={2} textAlign="center" color="text.secondary">
              No matching records found.
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DetailedAttendanceLogList;
